/**
 * Sprint Planning View
 *
 * Kanban-style sprint planning with:
 * - Backlog | Sprint | Done columns
 * - Drag & drop task assignment
 * - Sprint progress tracking
 * - Velocity and burndown charts
 */

import { useMemo, useRef, useState, useCallback, useEffect, type DragEvent } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  Play,
  CheckCircle,
  Plus,
  Target,
  TrendingUp,
  ChevronDown,
  Archive,
  BarChart3,
  Clock,
  Zap,
  MoreHorizontal,
  Sparkles,
  CircleDashed,
  Filter,
  FileText,
  Users,
} from 'lucide-react';
import clsx from 'clsx';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';

import type { Task, GroupingState, SortingState, Priority } from '@/types';
import { useSprints, type SprintVelocity, type BurndownDataPoint } from '@/hooks/useSprints';
import { useViewData } from '@/hooks/useViewData';
import { useTaskActions } from '@/hooks/useTaskActions';
import { useAppStore } from '@/store/appStore';
import {
  ViewToolbar,
  MultiFilterSelector,
} from '@/components/shared/view-toolbar';
import {
  StatusSelect,
  PrioritySelect,
  InlineDatePicker,
  AssigneesEditor,
  EditableText,
} from '@/components/shared/inline-editors';
import { getAllFilterOptions } from '@/utils/filterOptions';
import { useProfilesContext } from '@/context/ProfilesContext';
import { normalizeStatus } from '@/constants/taskStatuses';
import {
  createDefaultGroupingState,
  createDefaultSortingState,
} from '@/utils/grouping';
import type { SprintSummary, CreateSprintData } from '@/db/sprints';
import { formatDuration } from '@/utils/duration';
import { getInitials, getAvatarColor } from '@/utils/formatters';

// Column configuration for sprint board
interface SprintColumn {
  id: 'backlog' | 'sprint' | 'done';
  title: string;
  icon: React.ReactNode;
  color: string;
  bgClass: string;
  borderClass: string;
  dotColor: string;
}

const columns: SprintColumn[] = [
  {
    id: 'backlog',
    title: 'Backlog',
    icon: <Archive className="w-3.5 h-3.5" />,
    color: 'text-text-tertiary',
    bgClass: 'bg-surface-tertiary/30',
    borderClass: 'border-border-primary',
    dotColor: 'bg-text-quaternary',
  },
  {
    id: 'sprint',
    title: 'Sprint',
    icon: <Zap className="w-3.5 h-3.5" />,
    color: 'text-accent-blue',
    bgClass: 'bg-accent-blue/5',
    borderClass: 'border-accent-blue/20',
    dotColor: 'bg-accent-blue',
  },
  {
    id: 'done',
    title: 'Completato',
    icon: <Sparkles className="w-3.5 h-3.5" />,
    color: 'text-accent-emerald',
    bgClass: 'bg-accent-emerald/5',
    borderClass: 'border-accent-emerald/20',
    dotColor: 'bg-accent-emerald',
  },
];

export function SprintView() {
  const {
    filters,
    grouping,
    sorting,
    viewMode,
    availableAssignees,
    availableTags,
    onTaskClick,
    onFilterChange,
    onViewModeChange,
  } = useViewData();
  const { handleUpdateTask } = useTaskActions();

  // Get current space from path (first segment is the space ID)
  const currentSpaceId = useAppStore((state) => {
    const path = state.filters.path;
    if (!path) return null;
    return path.split('/')[0];
  });

  const {
    sprints,
    selectedSprint,
    setSelectedSprintId,
    createNewSprint,
    startSprintById,
    completeSprintById,
    assignTask,
    backlogTasks,
    sprintTasks,
    doneTasks,
    velocityData,
    burndownData,
    getSprintProgress,
    getSprintDaysRemaining,
    updateSprintData,
  } = useSprints({ spaceId: currentSpaceId });

  const [draggedTaskId, setDraggedTaskId] = useState<string | null>(null);
  const [dragOverColumn, setDragOverColumn] = useState<string | null>(null);
  const [showCreateSprint, setShowCreateSprint] = useState(false);
  const [showCharts, setShowCharts] = useState(true);
  // Initialize local state for future grouping/sorting features
  useState<GroupingState>(() => grouping || createDefaultGroupingState());
  useState<SortingState>(() => sorting || createDefaultSortingState());

  // Filter options
  const uniqueValues = useMemo(() => {
    const allTasks = [...backlogTasks, ...sprintTasks, ...doneTasks];
    return {
      statuses: Array.from(new Set(allTasks.map((t) => normalizeStatus(t.status)))).filter(Boolean),
      assignees: Array.from(
        new Set(allTasks.flatMap((t) => (t.assignees || []).filter(Boolean)))
      ).filter((a): a is string => a !== null),
      tags: Array.from(new Set(allTasks.flatMap((t) => (t.tags || []).filter(Boolean)))).filter(
        (t): t is string => t !== null
      ),
      priorities: Array.from(new Set(allTasks.map((t) => t.priority).filter(Boolean))),
    };
  }, [backlogTasks, sprintTasks, doneTasks]);

  const { resolveDisplayName } = useProfilesContext();
  const filterOptions = useMemo(() => {
    return getAllFilterOptions(
      uniqueValues.statuses,
      uniqueValues.assignees,
      uniqueValues.tags,
      uniqueValues.priorities as Priority[],
      resolveDisplayName,
    );
  }, [uniqueValues, resolveDisplayName]);

  // Drag handlers
  const handleDragStart = useCallback((e: DragEvent, taskId: string) => {
    setDraggedTaskId(taskId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', taskId);
  }, []);

  const handleDragEnd = useCallback(() => {
    setDraggedTaskId(null);
    setDragOverColumn(null);
  }, []);

  const handleDragOver = useCallback((e: DragEvent, columnId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverColumn(columnId);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOverColumn(null);
  }, []);

  const handleDrop = useCallback(
    async (e: DragEvent, columnId: SprintColumn['id']) => {
      e.preventDefault();
      const taskId = e.dataTransfer.getData('text/plain');

      if (taskId) {
        if (columnId === 'backlog') {
          // Remove from sprint
          await assignTask(taskId, null);
        } else if (columnId === 'sprint' && selectedSprint) {
          // Assign to current sprint
          await assignTask(taskId, selectedSprint.id);
        } else if (columnId === 'done' && selectedSprint) {
          // Assign to sprint and mark as completed
          await assignTask(taskId, selectedSprint.id);
          handleUpdateTask(taskId, { status: 'completed' });
        }
      }

      setDraggedTaskId(null);
      setDragOverColumn(null);
    },
    [assignTask, selectedSprint, handleUpdateTask]
  );

  // Get tasks for each column
  const getColumnTasks = useCallback(
    (columnId: SprintColumn['id']): Task[] => {
      switch (columnId) {
        case 'backlog':
          return backlogTasks;
        case 'sprint':
          return sprintTasks;
        case 'done':
          return doneTasks;
        default:
          return [];
      }
    },
    [backlogTasks, sprintTasks, doneTasks]
  );

  // Calculate story points for column
  const getColumnPoints = useCallback(
    (columnId: SprintColumn['id']): number => {
      const columnTasks = getColumnTasks(columnId);
      return columnTasks.reduce((sum, t) => sum + (t.storyPoints || 0), 0);
    },
    [getColumnTasks]
  );

  // Create new sprint handler
  const handleCreateSprint = useCallback(
    async (data: CreateSprintData) => {
      await createNewSprint(data);
      setShowCreateSprint(false);
    },
    [createNewSprint]
  );

  if (!currentSpaceId) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <Target className="w-12 h-12 text-text-quaternary mx-auto mb-4" />
          <h3 className="text-lg font-medium text-text-secondary mb-2">Seleziona un progetto</h3>
          <p className="text-sm text-text-tertiary">
            Per visualizzare gli sprint, seleziona prima un progetto dalla sidebar.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden animate-fade-in">
      {/* Toolbar */}
      <ViewToolbar
        onOpenSidebar={() => useAppStore.getState().setMobileSidebarOpen(true)}
        icon={<Target className="w-4 h-4 text-accent-violet" />}
        title="Sprint Planning"
        subtitle={`${backlogTasks.length + sprintTasks.length + doneTasks.length} task`}
        viewMode={viewMode}
        onViewModeChange={onViewModeChange}
        showViewSwitcher={!!onViewModeChange}
        leftContent={
          <div className="flex items-center gap-2 flex-wrap">
            {/* Sprint Selector */}
            <SprintSelector
              sprints={sprints}
              selectedSprint={selectedSprint}
              onSelect={setSelectedSprintId}
              onCreateNew={() => setShowCreateSprint(true)}
            />

            {/* Filters */}
            <MultiFilterSelector
              filters={{
                status: filters.status || [],
                assignees: filters.assignees || [],
                tags: filters.tags || [],
                priority: filters.priority || [],
              }}
              onFilterChange={onFilterChange}
              availableOptions={filterOptions}
              icon={<Filter className="w-3.5 h-3.5 text-text-tertiary" />}
            />

            {/* Charts toggle */}
            <button
              onClick={() => setShowCharts(!showCharts)}
              className={clsx(
                'flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-medium transition-all',
                showCharts
                  ? 'bg-accent-violet/10 text-accent-violet'
                  : 'text-text-tertiary hover:bg-surface-hover'
              )}
            >
              <BarChart3 className="w-3.5 h-3.5" />
              <span>Grafici</span>
            </button>
          </div>
        }
      />

      {/* Sprint Header */}
      {selectedSprint && (
        <SprintHeader
          sprint={selectedSprint}
          progress={getSprintProgress(selectedSprint)}
          daysRemaining={getSprintDaysRemaining(selectedSprint)}
          onStart={() => startSprintById(selectedSprint.id)}
          onComplete={() => completeSprintById(selectedSprint.id)}
          onUpdateNotes={(notes) => updateSprintData(selectedSprint.id, { retrospectiveNotes: notes })}
        />
      )}

      {/* Charts Section */}
      {showCharts && selectedSprint && (
        <div className="space-y-4 px-4 pb-4">
          <div className="grid grid-cols-2 gap-4">
            <VelocityChart data={velocityData} />
            <BurndownChart data={burndownData} sprint={selectedSprint} />
          </div>
          <CapacityChart
            tasks={[...sprintTasks, ...doneTasks]}
            sprint={selectedSprint}
            resolveDisplayName={resolveDisplayName}
          />
        </div>
      )}

      {/* Board Content */}
      <div className="flex-1 overflow-x-auto p-4 pt-0">
        <div className="flex gap-4 min-w-max h-full">
          {columns.map((column, columnIndex) => {
            const columnTasks = getColumnTasks(column.id);
            const columnPoints = getColumnPoints(column.id);

            return (
              <SprintColumn
                key={column.id}
                column={column}
                tasks={columnTasks}
                points={columnPoints}
                onTaskClick={onTaskClick}
                onUpdateTask={handleUpdateTask}
                availableAssignees={availableAssignees}
                availableTags={availableTags}
                delay={columnIndex * 50}
                draggedTaskId={draggedTaskId}
                isDragOver={dragOverColumn === column.id}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onDragOver={(e) => handleDragOver(e, column.id)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, column.id)}
              />
            );
          })}
        </div>
      </div>

      {/* Create Sprint Modal */}
      {showCreateSprint && currentSpaceId && (
        <CreateSprintModal
          spaceId={currentSpaceId}
          onClose={() => setShowCreateSprint(false)}
          onCreate={handleCreateSprint}
        />
      )}
    </div>
  );
}

// Sprint Selector Dropdown
interface SprintSelectorProps {
  sprints: SprintSummary[];
  selectedSprint: SprintSummary | null;
  onSelect: (id: string | null) => void;
  onCreateNew: () => void;
}

function SprintSelector({ sprints, selectedSprint, onSelect, onCreateNew }: SprintSelectorProps) {
  const [open, setOpen] = useState(false);

  const activeSprints = sprints.filter((s) => s.status === 'active');
  const plannedSprints = sprints.filter((s) => s.status === 'planned');
  const completedSprints = sprints.filter((s) => s.status === 'completed').slice(-3);

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-surface-secondary border border-border-primary hover:border-border-secondary transition-all text-sm"
      >
        <Target className="w-4 h-4 text-accent-violet" />
        <span className="font-medium text-text-primary">
          {selectedSprint?.name || 'Seleziona sprint'}
        </span>
        <ChevronDown className="w-3.5 h-3.5 text-text-tertiary" />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute top-full left-0 mt-1 w-64 bg-surface-primary border border-border-primary rounded-lg shadow-lg z-20 overflow-hidden">
            {/* Active Sprints */}
            {activeSprints.length > 0 && (
              <div className="p-2 border-b border-border-primary">
                <div className="text-[10px] font-semibold text-text-quaternary uppercase tracking-wide px-2 py-1">
                  Attivo
                </div>
                {activeSprints.map((sprint) => (
                  <button
                    key={sprint.id}
                    onClick={() => {
                      onSelect(sprint.id);
                      setOpen(false);
                    }}
                    className={clsx(
                      'w-full flex items-center gap-2 px-2 py-1.5 rounded text-left text-sm transition-colors',
                      selectedSprint?.id === sprint.id
                        ? 'bg-accent-violet/10 text-accent-violet'
                        : 'hover:bg-surface-hover text-text-primary'
                    )}
                  >
                    <Play className="w-3.5 h-3.5 text-accent-emerald" />
                    <span className="flex-1 truncate">{sprint.name}</span>
                    <span className="text-[10px] text-text-tertiary">
                      {sprint.completedTasks}/{sprint.totalTasks}
                    </span>
                  </button>
                ))}
              </div>
            )}

            {/* Planned Sprints */}
            {plannedSprints.length > 0 && (
              <div className="p-2 border-b border-border-primary">
                <div className="text-[10px] font-semibold text-text-quaternary uppercase tracking-wide px-2 py-1">
                  Pianificati
                </div>
                {plannedSprints.map((sprint) => (
                  <button
                    key={sprint.id}
                    onClick={() => {
                      onSelect(sprint.id);
                      setOpen(false);
                    }}
                    className={clsx(
                      'w-full flex items-center gap-2 px-2 py-1.5 rounded text-left text-sm transition-colors',
                      selectedSprint?.id === sprint.id
                        ? 'bg-accent-violet/10 text-accent-violet'
                        : 'hover:bg-surface-hover text-text-primary'
                    )}
                  >
                    <CircleDashed className="w-3.5 h-3.5 text-text-tertiary" />
                    <span className="flex-1 truncate">{sprint.name}</span>
                  </button>
                ))}
              </div>
            )}

            {/* Completed Sprints */}
            {completedSprints.length > 0 && (
              <div className="p-2 border-b border-border-primary">
                <div className="text-[10px] font-semibold text-text-quaternary uppercase tracking-wide px-2 py-1">
                  Completati
                </div>
                {completedSprints.map((sprint) => (
                  <button
                    key={sprint.id}
                    onClick={() => {
                      onSelect(sprint.id);
                      setOpen(false);
                    }}
                    className={clsx(
                      'w-full flex items-center gap-2 px-2 py-1.5 rounded text-left text-sm transition-colors',
                      selectedSprint?.id === sprint.id
                        ? 'bg-accent-violet/10 text-accent-violet'
                        : 'hover:bg-surface-hover text-text-primary'
                    )}
                  >
                    <CheckCircle className="w-3.5 h-3.5 text-accent-emerald" />
                    <span className="flex-1 truncate">{sprint.name}</span>
                    <span className="text-[10px] text-text-tertiary">
                      {sprint.completedStoryPoints} pts
                    </span>
                  </button>
                ))}
              </div>
            )}

            {/* Create New */}
            <div className="p-2">
              <button
                onClick={() => {
                  onCreateNew();
                  setOpen(false);
                }}
                className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-left text-sm text-accent-violet hover:bg-accent-violet/10 transition-colors"
              >
                <Plus className="w-3.5 h-3.5" />
                <span>Nuovo sprint</span>
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// Sprint Header with Progress
interface SprintHeaderProps {
  sprint: SprintSummary;
  progress: number;
  daysRemaining: number;
  onStart: () => void;
  onComplete: () => void;
  onUpdateNotes: (notes: string) => void;
}

function SprintHeader({ sprint, progress, daysRemaining, onStart, onComplete, onUpdateNotes }: SprintHeaderProps) {
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesDraft, setNotesDraft] = useState(sprint.retrospectiveNotes || '');
  const notesRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    setNotesDraft(sprint.retrospectiveNotes || '');
  }, [sprint.retrospectiveNotes]);

  useEffect(() => {
    if (editingNotes && notesRef.current) {
      notesRef.current.focus();
      notesRef.current.style.height = 'auto';
      notesRef.current.style.height = notesRef.current.scrollHeight + 'px';
    }
  }, [editingNotes]);
  return (
    <div className="px-4 pb-4">
      <div className="bg-surface-secondary/50 rounded-xl border border-border-primary p-4">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h2 className="text-lg font-semibold text-text-primary">{sprint.name}</h2>
              <span
                className={clsx(
                  'px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase',
                  sprint.status === 'active'
                    ? 'bg-accent-emerald/10 text-accent-emerald'
                    : sprint.status === 'completed'
                      ? 'bg-accent-blue/10 text-accent-blue'
                      : 'bg-surface-tertiary text-text-tertiary'
                )}
              >
                {sprint.status === 'active'
                  ? 'In corso'
                  : sprint.status === 'completed'
                    ? 'Completato'
                    : 'Pianificato'}
              </span>
            </div>
            {sprint.goal && (
              <p className="text-sm text-text-tertiary">{sprint.goal}</p>
            )}
          </div>

          <div className="flex items-center gap-2">
            {sprint.status === 'planned' && (
              <button
                onClick={onStart}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-accent-emerald text-white text-sm font-medium hover:bg-accent-emerald/90 transition-colors"
              >
                <Play className="w-3.5 h-3.5" />
                <span>Avvia sprint</span>
              </button>
            )}
            {sprint.status === 'active' && (
              <button
                onClick={onComplete}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-accent-violet text-white text-sm font-medium hover:bg-accent-violet/90 transition-colors"
              >
                <CheckCircle className="w-3.5 h-3.5" />
                <span>Completa sprint</span>
              </button>
            )}
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-4 gap-4 mb-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-accent-blue/10 flex items-center justify-center">
              <Target className="w-4 h-4 text-accent-blue" />
            </div>
            <div>
              <div className="text-lg font-semibold text-text-primary tabular-nums">
                {sprint.totalStoryPoints}
              </div>
              <div className="text-[10px] text-text-tertiary">Story Points</div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-accent-emerald/10 flex items-center justify-center">
              <CheckCircle className="w-4 h-4 text-accent-emerald" />
            </div>
            <div>
              <div className="text-lg font-semibold text-text-primary tabular-nums">
                {sprint.completedTasks}/{sprint.totalTasks}
              </div>
              <div className="text-[10px] text-text-tertiary">Task completati</div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-accent-violet/10 flex items-center justify-center">
              <TrendingUp className="w-4 h-4 text-accent-violet" />
            </div>
            <div>
              <div className="text-lg font-semibold text-text-primary tabular-nums">
                {sprint.completedStoryPoints}
              </div>
              <div className="text-[10px] text-text-tertiary">Punti completati</div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-accent-amber/10 flex items-center justify-center">
              <Clock className="w-4 h-4 text-accent-amber" />
            </div>
            <div>
              <div className="text-lg font-semibold text-text-primary tabular-nums">
                {daysRemaining >= 0 ? daysRemaining : '-'}
              </div>
              <div className="text-[10px] text-text-tertiary">Giorni rimanenti</div>
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="relative">
          <div className="h-2 bg-surface-tertiary rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-accent-violet to-accent-blue rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="absolute right-0 top-3 text-[10px] font-medium text-text-tertiary">
            {progress}% completato
          </div>
        </div>

        {/* Retrospective Notes - shown for completed or active sprints */}
        {(sprint.status === 'completed' || sprint.status === 'active') && (
          <div className="mt-4 pt-3 border-t border-border-primary">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-[11px] font-semibold text-text-tertiary uppercase tracking-wide flex items-center gap-1.5">
                <FileText className="w-3.5 h-3.5" />
                {sprint.status === 'completed' ? 'Retrospettiva' : 'Note sprint'}
              </h4>
              {!editingNotes && (
                <button
                  onClick={() => setEditingNotes(true)}
                  className="text-[10px] text-text-quaternary hover:text-accent-violet transition-colors"
                >
                  {sprint.retrospectiveNotes ? 'Modifica' : 'Aggiungi note'}
                </button>
              )}
            </div>
            {editingNotes ? (
              <div>
                <textarea
                  ref={notesRef}
                  value={notesDraft}
                  onChange={(e) => {
                    setNotesDraft(e.target.value);
                    e.target.style.height = 'auto';
                    e.target.style.height = e.target.scrollHeight + 'px';
                  }}
                  placeholder={sprint.status === 'completed'
                    ? 'Cosa è andato bene? Cosa migliorare? Azioni per il prossimo sprint...'
                    : 'Note, blocchi, osservazioni...'}
                  className="w-full px-3 py-2 rounded-lg bg-surface-tertiary border border-border-primary text-[12px] text-text-primary placeholder:text-text-quaternary focus:border-accent-violet focus:ring-1 focus:ring-accent-violet transition-all resize-none min-h-[80px]"
                />
                <div className="flex items-center gap-2 mt-2">
                  <button
                    onClick={() => {
                      onUpdateNotes(notesDraft);
                      setEditingNotes(false);
                    }}
                    className="px-3 py-1 rounded-md bg-accent-violet text-white text-[11px] font-medium hover:bg-accent-violet/90 transition-colors"
                  >
                    Salva
                  </button>
                  <button
                    onClick={() => {
                      setNotesDraft(sprint.retrospectiveNotes || '');
                      setEditingNotes(false);
                    }}
                    className="px-3 py-1 rounded-md text-[11px] text-text-tertiary hover:bg-surface-hover transition-colors"
                  >
                    Annulla
                  </button>
                </div>
              </div>
            ) : sprint.retrospectiveNotes ? (
              <div className="text-[12px] text-text-secondary whitespace-pre-wrap leading-relaxed">
                {sprint.retrospectiveNotes}
              </div>
            ) : (
              <p className="text-[11px] text-text-quaternary italic">Nessuna nota ancora</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Velocity Chart Component
interface VelocityChartProps {
  data: SprintVelocity[];
}

function VelocityChart({ data }: VelocityChartProps) {
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);

  if (data.length === 0) {
    return (
      <div className="bg-surface-secondary/50 rounded-xl border border-border-primary p-4">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="w-4 h-4 text-accent-violet" />
          <h3 className="text-sm font-semibold text-text-primary">Velocity</h3>
        </div>
        <div className="h-32 flex items-center justify-center text-sm text-text-tertiary">
          Completa almeno uno sprint per vedere la velocity
        </div>
      </div>
    );
  }

  const maxPoints = Math.max(...data.map((d) => Math.max(d.completedPoints, d.plannedPoints)));
  const avgVelocity = Math.round(data.reduce((sum, d) => sum + d.completedPoints, 0) / data.length);

  return (
    <div className="bg-surface-secondary/50 rounded-xl border border-border-primary p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-accent-violet" />
          <h3 className="text-sm font-semibold text-text-primary">Velocity</h3>
        </div>
        <div className="flex items-center gap-3 text-[10px]">
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-accent-violet" />
            <span className="text-text-tertiary">Completati</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-accent-blue/30" />
            <span className="text-text-tertiary">Pianificati</span>
          </div>
        </div>
      </div>

      <div className="relative h-32 flex items-end gap-2">
        {data.map((sprint, idx) => {
          const completionRate = sprint.plannedPoints > 0
            ? Math.round((sprint.completedPoints / sprint.plannedPoints) * 100)
            : 0;
          return (
            <div
              key={sprint.sprintId}
              className="flex-1 flex flex-col items-center gap-1 relative"
              onMouseEnter={() => setHoveredIdx(idx)}
              onMouseLeave={() => setHoveredIdx(null)}
            >
              {/* Tooltip */}
              {hoveredIdx === idx && (
                <div className="absolute -top-16 left-1/2 -translate-x-1/2 z-10 bg-surface-primary border border-border-primary rounded-lg shadow-lg px-2.5 py-1.5 whitespace-nowrap pointer-events-none">
                  <div className="text-[10px] font-medium text-text-primary mb-0.5">{sprint.sprintName}</div>
                  <div className="text-[9px] text-text-tertiary">
                    {sprint.completedPoints}/{sprint.plannedPoints} pts · {sprint.completedTasks}/{sprint.totalTasks} task · {completionRate}%
                  </div>
                </div>
              )}
              <div className="w-full flex items-end gap-0.5" style={{ height: '100px' }}>
                <div
                  className={clsx('flex-1 rounded-t transition-all', hoveredIdx === idx ? 'bg-accent-blue/30' : 'bg-accent-blue/20')}
                  style={{
                    height: `${maxPoints > 0 ? (sprint.plannedPoints / maxPoints) * 100 : 0}%`,
                    minHeight: sprint.plannedPoints > 0 ? '4px' : '0',
                  }}
                />
                <div
                  className={clsx('flex-1 rounded-t transition-all', hoveredIdx === idx ? 'bg-accent-violet brightness-110' : 'bg-accent-violet')}
                  style={{
                    height: `${maxPoints > 0 ? (sprint.completedPoints / maxPoints) * 100 : 0}%`,
                    minHeight: sprint.completedPoints > 0 ? '4px' : '0',
                  }}
                />
              </div>
              <div className="text-[9px] text-text-quaternary truncate w-full text-center">
                {sprint.sprintName.substring(0, 8)}
              </div>
            </div>
          );
        })}
      </div>

      {/* Average velocity */}
      <div className="mt-3 pt-3 border-t border-border-primary flex items-center justify-between">
        <span className="text-[10px] text-text-tertiary">Velocity media</span>
        <span className="text-sm font-semibold text-accent-violet tabular-nums">
          {avgVelocity} pts/sprint
        </span>
      </div>
    </div>
  );
}

// Burndown Chart Component
interface BurndownChartProps {
  data: BurndownDataPoint[];
  sprint: SprintSummary;
}

function BurndownChart({ data, sprint }: BurndownChartProps) {
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);
  const chartRef = useRef<HTMLDivElement>(null);

  if (data.length === 0) {
    return (
      <div className="bg-surface-secondary/50 rounded-xl border border-border-primary p-4">
        <div className="flex items-center gap-2 mb-4">
          <BarChart3 className="w-4 h-4 text-accent-blue" />
          <h3 className="text-sm font-semibold text-text-primary">Burndown</h3>
        </div>
        <div className="h-32 flex items-center justify-center text-sm text-text-tertiary">
          Avvia lo sprint per vedere il burndown chart
        </div>
      </div>
    );
  }

  const maxPoints = Math.max(...data.map((d) => d.idealRemaining));
  const actualData = data.filter((d) => d.actualRemaining >= 0);
  const chartPadding = { left: 8, right: 2, top: 2, bottom: 2 };
  const chartW = 100;
  const chartH = 50;
  const plotW = chartW - chartPadding.left - chartPadding.right;
  const plotH = chartH - chartPadding.top - chartPadding.bottom;

  const toX = (i: number, total: number) => chartPadding.left + (total > 1 ? (i / (total - 1)) * plotW : plotW / 2);
  const toY = (val: number) => chartPadding.top + plotH - (maxPoints > 0 ? (val / maxPoints) * plotH : 0);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!chartRef.current || data.length === 0) return;
    const rect = chartRef.current.getBoundingClientRect();
    const relX = (e.clientX - rect.left) / rect.width;
    const idx = Math.round(relX * (data.length - 1));
    setHoveredIdx(Math.max(0, Math.min(data.length - 1, idx)));
  };

  const hoveredPoint = hoveredIdx !== null ? data[hoveredIdx] : null;
  const hoveredIsActual = hoveredPoint ? hoveredPoint.actualRemaining >= 0 : false;

  return (
    <div className="bg-surface-secondary/50 rounded-xl border border-border-primary p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-accent-blue" />
          <h3 className="text-sm font-semibold text-text-primary">Burndown</h3>
        </div>
        <div className="flex items-center gap-3 text-[10px]">
          <div className="flex items-center gap-1">
            <div className="w-6 h-0.5 bg-accent-blue/50" style={{ borderStyle: 'dashed' }} />
            <span className="text-text-tertiary">Ideale</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-6 h-0.5 bg-accent-violet" />
            <span className="text-text-tertiary">Attuale</span>
          </div>
        </div>
      </div>

      {/* SVG Chart with hover interaction */}
      <div
        ref={chartRef}
        className="relative"
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setHoveredIdx(null)}
      >
        {/* Hover tooltip */}
        {hoveredPoint && hoveredIdx !== null && (
          <div
            className="absolute -top-10 z-10 bg-surface-primary border border-border-primary rounded-lg shadow-lg px-2.5 py-1.5 whitespace-nowrap pointer-events-none -translate-x-1/2"
            style={{ left: `${(hoveredIdx / (data.length - 1)) * 100}%` }}
          >
            <div className="text-[10px] font-medium text-text-primary">
              {format(hoveredPoint.date, 'dd MMM', { locale: it })}
            </div>
            <div className="text-[9px] text-text-tertiary">
              Ideale: {Math.round(hoveredPoint.idealRemaining)} pts
              {hoveredIsActual && <> · Attuale: {Math.round(hoveredPoint.actualRemaining)} pts</>}
            </div>
          </div>
        )}

        <svg className="w-full h-36" viewBox={`0 0 ${chartW} ${chartH}`} preserveAspectRatio="none">
          {/* Y-axis grid lines + labels */}
          {[0, 0.25, 0.5, 0.75, 1].map((frac) => {
            const y = chartPadding.top + plotH * (1 - frac);
            return (
              <g key={frac}>
                <line x1={chartPadding.left} y1={y} x2={chartW - chartPadding.right} y2={y} stroke="currentColor" strokeOpacity="0.07" />
                <text x={chartPadding.left - 1} y={y + 0.8} textAnchor="end" fill="currentColor" fillOpacity="0.3" fontSize="2.5">
                  {Math.round(maxPoints * frac)}
                </text>
              </g>
            );
          })}

          {/* Ideal line */}
          <line
            x1={toX(0, data.length)}
            y1={toY(maxPoints)}
            x2={toX(data.length - 1, data.length)}
            y2={toY(0)}
            stroke="rgb(var(--accent-blue))"
            strokeWidth="0.8"
            strokeDasharray="2,1.5"
            strokeOpacity="0.5"
          />

          {/* Area under actual line */}
          {actualData.length > 1 && (
            <polygon
              fill="rgb(var(--accent-violet))"
              fillOpacity="0.08"
              points={`${toX(0, data.length)},${toY(0)} ${actualData
                .map((point, i) => `${toX(i, data.length)},${toY(point.actualRemaining)}`)
                .join(' ')} ${toX(actualData.length - 1, data.length)},${toY(0)}`}
            />
          )}

          {/* Actual line */}
          {actualData.length > 1 && (
            <polyline
              fill="none"
              stroke="rgb(var(--accent-violet))"
              strokeWidth="1.2"
              strokeLinejoin="round"
              points={actualData
                .map((point, i) => `${toX(i, data.length)},${toY(point.actualRemaining)}`)
                .join(' ')}
            />
          )}

          {/* Data point dots on actual line */}
          {actualData.map((point, i) => (
            <circle
              key={i}
              cx={toX(i, data.length)}
              cy={toY(point.actualRemaining)}
              r={hoveredIdx === i ? '1.2' : '0.6'}
              fill="rgb(var(--accent-violet))"
              stroke="rgb(var(--accent-violet))"
              strokeWidth="0.3"
            />
          ))}

          {/* Hover vertical line */}
          {hoveredIdx !== null && (
            <line
              x1={toX(hoveredIdx, data.length)}
              y1={chartPadding.top}
              x2={toX(hoveredIdx, data.length)}
              y2={chartPadding.top + plotH}
              stroke="currentColor"
              strokeOpacity="0.15"
              strokeWidth="0.5"
              strokeDasharray="1,1"
            />
          )}
        </svg>
      </div>

      {/* Date labels */}
      <div className="flex justify-between mt-1 text-[9px] text-text-quaternary" style={{ paddingLeft: '8%', paddingRight: '2%' }}>
        {sprint.startDate && <span>{format(sprint.startDate, 'dd MMM', { locale: it })}</span>}
        {data.length > 2 && sprint.startDate && sprint.endDate && (
          <span>{format(sprint.startDate + (sprint.endDate - sprint.startDate) / 2, 'dd MMM', { locale: it })}</span>
        )}
        {sprint.endDate && <span>{format(sprint.endDate, 'dd MMM', { locale: it })}</span>}
      </div>

      {/* Summary stats */}
      <div className="mt-2 pt-2 border-t border-border-primary flex items-center justify-between">
        <span className="text-[10px] text-text-tertiary">Rimanenti</span>
        <span className="text-sm font-semibold text-accent-blue tabular-nums">
          {actualData.length > 0 ? Math.round(actualData[actualData.length - 1].actualRemaining) : sprint.totalStoryPoints} / {sprint.totalStoryPoints} pts
        </span>
      </div>
    </div>
  );
}

// Capacity Planning Chart
interface CapacityChartProps {
  tasks: Task[];
  sprint: SprintSummary;
  resolveDisplayName: (id: string) => string;
}

interface MemberCapacity {
  userId: string;
  name: string;
  estimatedMs: number;
  spentMs: number;
  availableMs: number;
  taskCount: number;
}

function CapacityChart({ tasks, sprint, resolveDisplayName }: CapacityChartProps) {
  const DEFAULT_HOURS_PER_DAY = 8;

  const members = useMemo((): MemberCapacity[] => {
    if (!sprint.startDate || !sprint.endDate) return [];

    // Calculate working days in sprint (exclude weekends)
    const startDate = new Date(sprint.startDate);
    const endDate = new Date(sprint.endDate);
    let workingDays = 0;
    const d = new Date(startDate);
    while (d <= endDate) {
      const day = d.getDay();
      if (day !== 0 && day !== 6) workingDays++;
      d.setDate(d.getDate() + 1);
    }
    if (workingDays === 0) workingDays = 1;

    const availableMs = workingDays * DEFAULT_HOURS_PER_DAY * 3600000;

    // Gather all unique assignees
    const memberMap = new Map<string, { estimatedMs: number; spentMs: number; taskCount: number }>();

    for (const task of tasks) {
      const assignees = task.assignees?.filter(Boolean) || [];
      if (assignees.length === 0) continue;

      for (const userId of assignees) {
        if (!memberMap.has(userId)) {
          memberMap.set(userId, { estimatedMs: 0, spentMs: 0, taskCount: 0 });
        }
        const m = memberMap.get(userId)!;
        m.taskCount++;

        // Per-assignee estimate if available, otherwise split evenly
        if (task.timeEstimatesByAssignee?.[userId] !== undefined) {
          m.estimatedMs += task.timeEstimatesByAssignee[userId];
        } else if (task.timeEstimated) {
          m.estimatedMs += task.timeEstimated / assignees.length;
        }

        // spentMs is no longer derived from task.timeSpent; use time_entries instead
        // (kept as 0 so the capacity chart still renders without errors)
      }
    }

    return Array.from(memberMap.entries())
      .map(([userId, data]) => ({
        userId,
        name: resolveDisplayName(userId),
        estimatedMs: data.estimatedMs,
        spentMs: data.spentMs,
        availableMs,
        taskCount: data.taskCount,
      }))
      .sort((a, b) => b.estimatedMs - a.estimatedMs);
  }, [tasks, sprint, resolveDisplayName]);

  if (members.length === 0) {
    return (
      <div className="bg-surface-secondary/50 rounded-xl border border-border-primary p-4">
        <div className="flex items-center gap-2 mb-4">
          <Users className="w-4 h-4 text-accent-emerald" />
          <h3 className="text-sm font-semibold text-text-primary">Capacity</h3>
        </div>
        <div className="h-16 flex items-center justify-center text-sm text-text-tertiary">
          Assegna task ai membri per vedere la capacity
        </div>
      </div>
    );
  }

  const maxMs = Math.max(...members.map((m) => Math.max(m.estimatedMs, m.availableMs, m.spentMs)));

  return (
    <div className="bg-surface-secondary/50 rounded-xl border border-border-primary p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-accent-emerald" />
          <h3 className="text-sm font-semibold text-text-primary">Capacity</h3>
        </div>
        <div className="flex items-center gap-3 text-[10px]">
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-accent-violet" />
            <span className="text-text-tertiary">Stimato</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-accent-emerald" />
            <span className="text-text-tertiary">Effettivo</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-6 h-0.5 border-t border-dashed border-text-quaternary" />
            <span className="text-text-tertiary">Disponibile</span>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        {members.map((member) => {
          const loadPct = member.availableMs > 0 ? (member.estimatedMs / member.availableMs) * 100 : 0;
          const _spentPct = member.availableMs > 0 ? (member.spentMs / member.availableMs) * 100 : 0;
          const isOverloaded = loadPct > 100;
          const barMax = maxMs > 0 ? maxMs : 1;

          return (
            <div key={member.userId} className="group">
              <div className="flex items-center gap-2 mb-1">
                {/* Avatar */}
                <div
                  className="w-5 h-5 rounded-full flex items-center justify-center text-[8px] font-semibold text-white shrink-0"
                  style={{ backgroundColor: getAvatarColor(member.name) }}
                >
                  {getInitials(member.name)}
                </div>
                <span className="text-[11px] text-text-primary font-medium truncate flex-1">
                  {member.name}
                </span>
                <span className="text-[9px] text-text-quaternary tabular-nums">
                  {member.taskCount} task
                </span>
                <span className={clsx(
                  'text-[10px] font-semibold tabular-nums',
                  isOverloaded ? 'text-red-400' : loadPct > 80 ? 'text-amber-400' : 'text-accent-emerald'
                )}>
                  {Math.round(loadPct)}%
                </span>
              </div>

              {/* Capacity bar */}
              <div className="relative h-4 bg-surface-hover rounded-full overflow-hidden">
                {/* Available capacity marker */}
                <div
                  className="absolute top-0 bottom-0 border-r-2 border-dashed border-text-quaternary/40 z-10"
                  style={{ left: `${Math.min((member.availableMs / barMax) * 100, 100)}%` }}
                />

                {/* Estimated bar */}
                <div
                  className={clsx(
                    'absolute top-0 bottom-0 left-0 rounded-full transition-all',
                    isOverloaded ? 'bg-red-500/30' : 'bg-accent-violet/25'
                  )}
                  style={{ width: `${Math.min((member.estimatedMs / barMax) * 100, 100)}%` }}
                />

                {/* Spent bar (on top) */}
                <div
                  className="absolute top-0 bottom-0 left-0 rounded-full bg-accent-emerald/60 transition-all"
                  style={{ width: `${Math.min((member.spentMs / barMax) * 100, 100)}%` }}
                />
              </div>

              {/* Time labels */}
              <div className="flex items-center justify-between mt-0.5">
                <span className="text-[9px] text-text-quaternary">
                  {formatDuration(member.spentMs, { showZero: true })} / {formatDuration(member.estimatedMs, { showZero: true })} stimato
                </span>
                <span className="text-[9px] text-text-quaternary">
                  {formatDuration(member.availableMs)} disponibile
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Team totals */}
      <div className="mt-3 pt-3 border-t border-border-primary grid grid-cols-3 gap-4">
        <div>
          <div className="text-[9px] text-text-quaternary">Stimato totale</div>
          <div className="text-sm font-semibold text-accent-violet tabular-nums">
            {formatDuration(members.reduce((s, m) => s + m.estimatedMs, 0), { showZero: true })}
          </div>
        </div>
        <div>
          <div className="text-[9px] text-text-quaternary">Effettivo totale</div>
          <div className="text-sm font-semibold text-accent-emerald tabular-nums">
            {formatDuration(members.reduce((s, m) => s + m.spentMs, 0), { showZero: true })}
          </div>
        </div>
        <div>
          <div className="text-[9px] text-text-quaternary">Disponibile</div>
          <div className="text-sm font-semibold text-text-secondary tabular-nums">
            {formatDuration(members.length > 0 ? members[0].availableMs : 0)} / membro
          </div>
        </div>
      </div>
    </div>
  );
}

// Sprint Column Component
interface SprintColumnProps {
  column: SprintColumn;
  tasks: Task[];
  points: number;
  onTaskClick?: (task: Task) => void;
  onUpdateTask?: (taskId: string, updates: Partial<Task>) => void;
  availableAssignees?: string[];
  availableTags?: string[];
  delay: number;
  draggedTaskId: string | null;
  isDragOver: boolean;
  onDragStart: (e: DragEvent, taskId: string) => void;
  onDragEnd: () => void;
  onDragOver: (e: DragEvent) => void;
  onDragLeave: () => void;
  onDrop: (e: DragEvent) => void;
}

function SprintColumn({
  column,
  tasks,
  points,
  onTaskClick,
  onUpdateTask,
  availableAssignees = [],
  availableTags = [],
  delay,
  draggedTaskId,
  isDragOver,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDragLeave,
  onDrop,
}: SprintColumnProps) {
  const parentRef = useRef<HTMLDivElement>(null);

  const rowVirtualizer = useVirtualizer({
    count: tasks.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 100,
    overscan: 5,
  });

  return (
    <div
      className={clsx(
        'w-80 flex flex-col bg-surface-secondary/30 rounded-xl border animate-slide-in-up overflow-hidden transition-all',
        isDragOver
          ? `${column.borderClass} ring-2 ring-accent-violet/30 bg-accent-violet/5`
          : 'border-border-primary'
      )}
      style={{ animationDelay: `${delay}ms` }}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      {/* Column Header */}
      <div className={clsx('flex items-center gap-2.5 px-3 py-2.5', column.bgClass)}>
        <div className={clsx('w-2 h-2 rounded-full', column.dotColor)} />
        <h3 className={clsx('text-[11px] font-semibold uppercase tracking-wide', column.color)}>
          {column.title}
        </h3>
        <span
          className={clsx(
            'px-2 py-0.5 rounded-full text-[10px] font-semibold tabular-nums',
            column.bgClass,
            column.color
          )}
        >
          {tasks.length}
        </span>
        <span className="ml-auto text-[10px] text-text-tertiary tabular-nums">{points} pts</span>
        <button className="p-1 rounded hover:bg-surface-hover transition-colors">
          <MoreHorizontal className="w-3.5 h-3.5 text-text-quaternary" />
        </button>
      </div>

      {/* Virtualized Tasks */}
      <div
        ref={parentRef}
        className="flex-1 overflow-y-auto p-2"
        style={{ maxHeight: 'calc(100vh - 350px)' }}
      >
        {tasks.length === 0 ? (
          <div
            className={clsx(
              'flex items-center justify-center h-24 text-[11px] rounded-lg border-2 border-dashed transition-all',
              isDragOver
                ? 'text-accent-violet border-accent-violet/40 bg-accent-violet/5'
                : 'text-text-quaternary border-transparent'
            )}
          >
            {isDragOver ? 'Rilascia qui' : 'Nessun task'}
          </div>
        ) : (
          <div
            style={{
              height: `${rowVirtualizer.getTotalSize()}px`,
              width: '100%',
              position: 'relative',
            }}
          >
            {rowVirtualizer.getVirtualItems().map((virtualRow) => {
              const task = tasks[virtualRow.index];
              const isDragging = draggedTaskId === task.id;

              return (
                <div
                  key={`${task.id}-${virtualRow.index}`}
                  data-index={virtualRow.index}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    transform: `translateY(${virtualRow.start}px)`,
                    paddingBottom: '8px',
                  }}
                >
                  <SprintTaskCard
                    task={task}
                    onClick={() => onTaskClick?.(task)}
                    onUpdate={onUpdateTask}
                    availableAssignees={availableAssignees}
                    availableTags={availableTags}
                    isDragging={isDragging}
                    onDragStart={(e) => onDragStart(e, task.id)}
                    onDragEnd={onDragEnd}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// Sprint Task Card
interface SprintTaskCardProps {
  task: Task;
  onClick?: () => void;
  onUpdate?: (taskId: string, updates: Partial<Task>) => void;
  availableAssignees?: string[];
  availableTags?: string[];
  isDragging?: boolean;
  onDragStart?: (e: DragEvent<HTMLDivElement>) => void;
  onDragEnd?: () => void;
}

function SprintTaskCard({
  task,
  onClick,
  onUpdate,
  availableAssignees = [],
  isDragging,
  onDragStart,
  onDragEnd,
}: SprintTaskCardProps) {
  const canEdit = !!onUpdate;

  const handleUpdate = (updates: Partial<Task>) => {
    if (onUpdate) {
      onUpdate(task.id, updates);
    }
  };

  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      className={clsx(
        'p-3 rounded-lg bg-surface-primary border border-border-primary hover:border-border-secondary hover:shadow-lg cursor-grab active:cursor-grabbing transition-all group',
        isDragging && 'opacity-50 scale-95 shadow-xl ring-2 ring-accent-violet/30'
      )}
    >
      {/* Header: Story Points + Priority */}
      <div
        className="flex items-center justify-between mb-2"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2">
          {/* Story Points */}
          <div className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-accent-violet/10 text-accent-violet text-[10px] font-semibold">
            <Target className="w-3 h-3" />
            <span>{task.storyPoints || 0}</span>
          </div>
          <StatusSelect
            value={task.status}
            onChange={(status) => handleUpdate({ status })}
            size="sm"
            showLabel={false}
            disabled={!canEdit}
          />
        </div>
        <PrioritySelect
          value={task.priority ?? null}
          onChange={(priority) => handleUpdate({ priority })}
          size="sm"
          showLabel={false}
          disabled={!canEdit}
        />
      </div>

      {/* Task Name */}
      <div className="mb-2" onClick={(e) => e.stopPropagation()}>
        {canEdit ? (
          <EditableText
            value={task.name}
            onChange={(name) => handleUpdate({ name })}
            className="text-[12px] font-medium leading-snug line-clamp-2 text-text-primary"
            inputClassName="text-[12px] font-medium"
          />
        ) : (
          <h4
            className="text-[12px] font-medium leading-snug line-clamp-2 text-text-primary cursor-pointer hover:text-accent-violet transition-colors"
            onClick={onClick}
          >
            {task.name}
          </h4>
        )}
      </div>

      {/* Tags */}
      {task.tags && task.tags.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2">
          {task.tags.slice(0, 2).map((tag) => (
            <span
              key={tag}
              className="px-1.5 py-0.5 rounded text-[9px] font-medium bg-surface-tertiary text-text-secondary"
            >
              {tag}
            </span>
          ))}
          {task.tags.length > 2 && (
            <span className="px-1.5 py-0.5 rounded text-[9px] font-medium text-text-tertiary">
              +{task.tags.length - 2}
            </span>
          )}
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between pt-2 border-t border-border-primary/50">
        <InlineDatePicker
          value={task.dueDate}
          onChange={(dueDate) => handleUpdate({ dueDate })}
          size="sm"
          showTime={false}
          disabled={!canEdit}
        />
        <AssigneesEditor
          assignees={task.assignees || []}
          availableAssignees={availableAssignees}
          onAdd={(a) => handleUpdate({ assignees: [...(task.assignees || []), a] })}
          onRemove={(a) =>
            handleUpdate({ assignees: (task.assignees || []).filter((x) => x !== a) })
          }
          size="sm"
          maxDisplay={2}
          disabled={!canEdit}
        />
      </div>
    </div>
  );
}

// Create Sprint Modal
interface CreateSprintModalProps {
  spaceId: string;
  onClose: () => void;
  onCreate: (data: CreateSprintData) => Promise<void>;
}

function CreateSprintModal({ spaceId, onClose, onCreate }: CreateSprintModalProps) {
  const [name, setName] = useState('');
  const [goal, setGoal] = useState('');
  const [startDate, setStartDate] = useState<number | null>(null);
  const [endDate, setEndDate] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    setLoading(true);
    try {
      await onCreate({
        name: name.trim(),
        spaceId,
        goal: goal.trim() || undefined,
        startDate,
        endDate,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-surface-primary rounded-xl border border-border-primary shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between p-4 border-b border-border-primary">
          <h2 className="text-lg font-semibold text-text-primary">Nuovo Sprint</h2>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-surface-hover transition-colors"
          >
            <Plus className="w-5 h-5 text-text-tertiary rotate-45" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1">
              Nome sprint *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="es. Sprint 1"
              className="w-full px-3 py-2 rounded-lg bg-surface-secondary border border-border-primary focus:border-accent-violet focus:outline-none text-sm"
              autoFocus
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1">
              Obiettivo
            </label>
            <textarea
              value={goal}
              onChange={(e) => setGoal(e.target.value)}
              placeholder="Descrivi l'obiettivo dello sprint..."
              rows={2}
              className="w-full px-3 py-2 rounded-lg bg-surface-secondary border border-border-primary focus:border-accent-violet focus:outline-none text-sm resize-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">
                Data inizio
              </label>
              <InlineDatePicker
                value={startDate}
                onChange={(ms) => setStartDate(ms)}
                label="Seleziona data"
                showTime={false}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">
                Data fine
              </label>
              <InlineDatePicker
                value={endDate}
                onChange={(ms) => setEndDate(ms)}
                label="Seleziona data"
                showTime={false}
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-sm font-medium text-text-secondary hover:bg-surface-hover transition-colors"
            >
              Annulla
            </button>
            <button
              type="submit"
              disabled={!name.trim() || loading}
              className="px-4 py-2 rounded-lg text-sm font-medium bg-accent-violet text-white hover:bg-accent-violet/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Creazione...' : 'Crea sprint'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
